import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Key, BarChart3, CreditCard, BookOpen, Settings, LogOut, ChevronRight, Bell, Search, Shield, ShieldCheck, HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL = "https://assets-h2.izmocars.com/dealerlogos/qcoresecure.com.png";

const NAV_ITEMS = [
  { path: "/dashboard", label: "nav.dashboard", icon: LayoutDashboard },
  { path: "/dashboard/api-keys", label: "nav.api_keys", icon: Key },
  { path: "/dashboard/analytics", label: "nav.analytics", icon: BarChart3 },
  { path: "/dashboard/billing", label: "nav.billing", icon: CreditCard },
  { path: "/dashboard/api-docs", label: "nav.api_docs", icon: BookOpen },
  { path: "/dashboard/security", label: "nav.security", icon: ShieldCheck },
  { path: "/dashboard/settings", label: "nav.settings", icon: Settings },
  { path: "/dashboard/help", label: "nav.help", icon: HelpCircle },
];

export default function CustomerLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { t } = useLanguage();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = user ? `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase() : "U";

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="customer-layout">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-slate-200 flex flex-col transition-all duration-200 fixed top-0 left-0 bottom-0 z-40`} data-testid="customer-sidebar">
        <div className={`h-16 border-b border-slate-200 flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'px-5'}`}>
          <Link to="/" className="flex items-center gap-2.5" data-testid="sidebar-logo">
            {sidebarCollapsed ? (
              <div className="w-7 h-7 bg-cyan-500 flex items-center justify-center flex-shrink-0">
                <span className="text-black font-bold text-[10px]">QC</span>
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 bg-slate-900 flex items-center justify-center flex-shrink-0">
                  <span className="text-cyan-400 font-bold text-[10px]">QC</span>
                </div>
                <span className="font-bold text-sm tracking-tight text-slate-900">QCORESECURE</span>
              </div>
            )}
          </Link>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'} mb-6`}>
            {!sidebarCollapsed && <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-medium px-3 mb-2">Platform</div>}
            {NAV_ITEMS.map(item => {
              const isActive = location.pathname === item.path || (item.path === "/dashboard" && location.pathname === "/dashboard");
              const label = t(item.label);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 mb-0.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  } ${sidebarCollapsed ? 'justify-center' : ''}`}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!sidebarCollapsed && <span>{label}</span>}
                </Link>
              );
            })}
          </div>
          {!sidebarCollapsed && user && ["super_admin", "admin"].includes(user.role) && (
            <div className="px-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-medium px-3 mb-2">Admin</div>
              <Link to="/admin" className="flex items-center gap-3 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors" data-testid="nav-admin-portal">
                <Shield className="w-4 h-4" />
                <span>{t("nav.admin_portal")}</span>
                <ChevronRight className="w-3 h-3 ml-auto text-slate-400" />
              </Link>
            </div>
          )}
        </nav>
        <div className={`border-t border-slate-200 p-3 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
          {sidebarCollapsed ? (
            <button className="p-2 text-slate-400 hover:text-slate-600" data-testid="sidebar-collapse-btn" onClick={() => setSidebarCollapsed(false)}>
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex items-center gap-3 px-2">
              <div className="w-8 h-8 bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">{initials}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{user ? `${user.first_name} ${user.last_name}` : "User"}</div>
                <div className="text-xs text-slate-400 truncate">{user?.organization || ""}</div>
              </div>
              <button onClick={handleLogout} className="p-1 text-slate-400 hover:text-slate-600" data-testid="sidebar-logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} transition-all duration-200`}>
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30" data-testid="customer-topbar">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors" data-testid="sidebar-toggle-btn">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
            </button>
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder={t("action.search") + "..."} className="w-64 h-9 pl-9 bg-slate-50 border-slate-200 text-sm" data-testid="topbar-search" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="compact" />
            <Badge variant="outline" className="text-xs font-mono bg-emerald-50 text-emerald-700 border-emerald-200 hidden md:flex" data-testid="topbar-status-badge">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5 animate-pulse" />
              {t("status.all_systems_operational")}
            </Badge>
            <button className="relative p-2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="topbar-notifications-btn">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
